<template>
  <div class="agent-commission-withdrawal">
    <el-card shadow="never" class="summary-card">
      <div class="summary-row">
        <div class="summary-item">
          <div class="label">可提现余额</div>
          <div class="value">¥{{ formatYuan(bindStatus.totalBalance) }}</div>
        </div>
        <div class="summary-item">
          <div class="label">支付宝授权</div>
          <div class="value status">
            <el-tag v-if="bindStatus.bound" type="success" size="small">已绑定</el-tag>
            <el-tag v-else type="warning" size="small">未绑定</el-tag>
            <span v-if="bindStatus.alipayNickName" class="nick">{{ bindStatus.alipayNickName }}</span>
          </div>
        </div>
        <div class="actions">
          <el-button type="primary" plain :loading="authLoading" @click="handleAuthorize">
            {{ bindStatus.bound ? '重新授权' : '授权支付宝' }}
          </el-button>
          <el-button type="success" :disabled="!bindStatus.bound" @click="openApply">申请提现</el-button>
        </div>
      </div>
      <el-alert
        class="min-amount-tip"
        type="info"
        :closable="false"
        show-icon
        title="提现金额单笔最低 0.1 元，低于该金额将无法打款。"
      />
    </el-card>

    <el-card shadow="never" class="table-card">
      <div slot="header" class="card-header">
        <span>我的提现记录</span>
        <el-button type="text" icon="el-icon-refresh" @click="fetchRecords">刷新</el-button>
      </div>
      <el-table v-loading="loading" :data="records" border stripe>
        <el-table-column prop="id" label="ID" width="80" align="center" />
        <el-table-column label="金额" width="120" align="center">
          <template slot-scope="scope">¥{{ formatYuan(scope.row.amount) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === 0" type="warning" size="small">待审核</el-tag>
            <el-tag v-else-if="scope.row.status === 1" type="success" size="small">成功</el-tag>
            <el-tag v-else-if="scope.row.status === 2" type="danger" size="small">已拒绝</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="transferNo" label="支付宝单号" min-width="180" show-overflow-tooltip />
        <el-table-column label="申请时间" width="180" align="center">
          <template slot-scope="scope">{{ formatDateTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="审核时间" width="180" align="center">
          <template slot-scope="scope">{{ formatDateTime(scope.row.auditTime) }}</template>
        </el-table-column>
        <el-table-column prop="auditRemark" label="备注" min-width="140" show-overflow-tooltip />
      </el-table>
      <el-pagination
        class="pager"
        background
        layout="total, prev, pager, next"
        :current-page.sync="pagination.page"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        @current-change="fetchRecords"
      />
    </el-card>

    <el-dialog title="申请提现" :visible.sync="applyVisible" width="400px" append-to-body>
      <el-form label-width="90px">
        <el-form-item label="提现金额">
          <el-input-number
            v-model="applyYuan"
            :min="0.01"
            :precision="2"
            :step="0.01"
            style="width: 100%;"
          />
          <div class="hint">单位：元。将按 1元=1000 金币扣减余额。</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="applyVisible = false">取消</el-button>
        <el-button type="primary" :loading="applyLoading" @click="confirmApply">提交</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { get, post } from '@/utils/http'

export default {
  name: 'AgentCommissionWithdrawal',
  data() {
    return {
      bindStatus: {
        bound: false,
        alipayNickName: '',
        totalBalance: 0
      },
      authLoading: false,
      loading: false,
      records: [],
      pagination: { page: 1, pageSize: 10, total: 0 },
      applyVisible: false,
      applyYuan: undefined,
      applyLoading: false
    }
  },
  mounted() {
    this.refreshAll()
  },
  methods: {
    async refreshAll() {
      await Promise.all([this.fetchBindStatus(), this.fetchRecords()])
    },
    async fetchBindStatus() {
      try {
        const res = await get('/admin/alipay/bind-status')
        this.bindStatus = res.data || this.bindStatus
      } catch (e) {
        this.$message.error(e.message || '获取绑定状态失败')
      }
    },
    async fetchRecords() {
      this.loading = true
      try {
        const res = await get('/admin/agentWithdrawal/list', {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize
        })
        const data = res.data || {}
        this.records = data.records || []
        this.pagination.total = Number(data.total || 0)
      } catch (e) {
        this.$message.error(e.message || '加载提现记录失败')
      } finally {
        this.loading = false
      }
    },
    async handleAuthorize() {
      this.authLoading = true
      try {
        const res = await get('/admin/alipay/auth-url')
        const url = res.data && res.data.url
        if (!url) {
          throw new Error('未获取到授权链接')
        }
        window.location.href = url
      } catch (e) {
        this.$message.error(e.message || '发起授权失败')
        this.authLoading = false
      }
    },
    openApply() {
      this.applyYuan = undefined
      this.applyVisible = true
    },
    async confirmApply() {
      const yuan = Number(this.applyYuan)
      if (!this.applyYuan || Number.isNaN(yuan) || yuan <= 0) {
        this.$message.warning('请输入提现金额')
        return
      }
      if (yuan < 0.1) {
        this.$message.warning('提现金额单笔最低 0.1 元，请重新填写')
        return
      }
      const amount = Math.round(yuan * 1000)
      this.applyLoading = true
      try {
        await post('/admin/agentWithdrawal/apply', { amount })
        this.$message.success('提现申请已提交')
        this.applyVisible = false
        this.refreshAll()
      } catch (e) {
        this.$message.error(e.message || '申请失败')
      } finally {
        this.applyLoading = false
      }
    },
    formatYuan(coins) {
      return (Number(coins || 0) / 1000).toFixed(2)
    },
    formatDateTime(v) {
      if (!v) return '-'
      return String(v).replace('T', ' ')
    }
  }
}
</script>

<style scoped>
.summary-card {
  margin-bottom: 16px;
}
.summary-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
}
.summary-item .label {
  color: #909399;
  font-size: 13px;
  margin-bottom: 6px;
}
.summary-item .value {
  font-size: 22px;
  font-weight: 600;
  color: #303133;
}
.summary-item .value.status {
  font-size: 14px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 8px;
}
.nick {
  color: #606266;
}
.actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}
.min-amount-tip {
  margin-top: 16px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.pager {
  margin-top: 16px;
  text-align: right;
}
.hint {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
}
</style>
